/**
 * Family Message Overlay Component
 */

import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { MessageCircle, Save, Send } from "lucide-react";
import { toast } from "sonner";

export function FamilyMessageOverlay({ patientId, patientName, onSave, onClose }) {
  const [formData, setFormData] = useState({
    recipient: "",
    recipient_relationship: "",
    message_type: "",
    subject: "",
    message: "",
    urgent: false,
    response_requested: false,
    preferred_contact: "email",
  });

  const handleSave = () => {
    if (!formData.recipient || !formData.message) {
      toast.error("Please fill in recipient and message");
      return;
    }
    toast.success("Message sent to family member");
    if (onSave) onSave(formData);
    if (onClose) onClose();
  };

  return (
    <div className="space-y-4 p-4" data-testid="family-message-overlay">
      <div className="flex items-center gap-2 mb-4">
        <MessageCircle className="w-5 h-5 text-cyan-400" />
        <h3 className="font-semibold text-white">Family Message</h3>
        <Badge variant="outline" className="border-slate-600 text-slate-300">{patientName}</Badge>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-200">Recipient Name</Label>
          <Input 
            value={formData.recipient} 
            onChange={(e) => setFormData({...formData, recipient: e.target.value})} 
            placeholder="Family member name" 
            className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-200">Relationship</Label>
          <Select value={formData.recipient_relationship} onValueChange={(v) => setFormData({...formData, recipient_relationship: v})}>
            <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="spouse">Spouse</SelectItem>
              <SelectItem value="child">Child</SelectItem>
              <SelectItem value="parent">Parent</SelectItem>
              <SelectItem value="sibling">Sibling</SelectItem>
              <SelectItem value="caregiver">Primary Caregiver</SelectItem>
              <SelectItem value="poa">POA/Healthcare Proxy</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-200">Message Type</Label>
          <Select value={formData.message_type} onValueChange={(v) => setFormData({...formData, message_type: v})}>
            <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue placeholder="Select type..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="update">Status Update</SelectItem>
              <SelectItem value="question">Question/Inquiry</SelectItem>
              <SelectItem value="care_coordination">Care Coordination</SelectItem>
              <SelectItem value="education">Education/Resources</SelectItem>
              <SelectItem value="scheduling">Scheduling</SelectItem>
              <SelectItem value="comfort">Comfort/Support</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-slate-200">Preferred Contact</Label>
          <Select value={formData.preferred_contact} onValueChange={(v) => setFormData({...formData, preferred_contact: v})}>
            <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="phone">Phone Call</SelectItem>
              <SelectItem value="portal">Family Portal</SelectItem>
              <SelectItem value="sms">Text Message</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Subject</Label>
        <Input 
          value={formData.subject} 
          onChange={(e) => setFormData({...formData, subject: e.target.value})} 
          placeholder="Message subject" 
          className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Message</Label>
        <Textarea 
          value={formData.message} 
          onChange={(e) => setFormData({...formData, message: e.target.value})} 
          placeholder="Type your message to the family member..." 
          rows={4} 
          className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700">
          <div>
            <Label className="text-slate-200">Urgent Message?</Label>
            <p className="text-xs text-slate-400">Mark as high priority</p>
          </div>
          <Switch checked={formData.urgent} onCheckedChange={(c) => setFormData({...formData, urgent: c})} />
        </div>
        <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700">
          <div>
            <Label className="text-slate-200">Response Requested?</Label>
            <p className="text-xs text-slate-400">Ask for reply</p>
          </div>
          <Switch checked={formData.response_requested} onCheckedChange={(c) => setFormData({...formData, response_requested: c})} />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300 hover:bg-slate-800">Cancel</Button>
        <Button onClick={handleSave} className="bg-teal-600 hover:bg-teal-700"><Send className="w-4 h-4 mr-1" />Send Message</Button>
      </div>
    </div>
  );
}

export default FamilyMessageOverlay;
